import { css } from "@emotion/react";

import { Margin, Text } from "@zapier/design-system";

import {
  Currency,
  defaultCurrency,
  formatPriceToBeInCurrency,
} from "@zapier/currency";

export type PriceProps = {
  /** Price in USD pennies (i.e. hundredths of a USD dollar) */
  children: number;
  /** Currency to show price in */
  currency?: Currency;
  isAnnual?: boolean;
};

const root = css`
  display: flex;
  margin: 0px;
`;

/**
 * Returns the decimal separator for the user's locale. Examples:
 *   - Returns . for English
 *   - Returns , for Swedish
 */
export const getDecimalSeparator = () => {
  const value = 1.1;
  return value.toLocaleString().substring(1, 2);
};

export const Price = ({
  currency = defaultCurrency,
  isAnnual,
  children,
}: PriceProps) => {
  const price = formatPriceToBeInCurrency(
    isAnnual ? children / 12 : children,
    currency,
    false,
    {
      maxDecimals: 2,
      minDecimals: 0,
      useRelativeRounding: false,
    }
  );

  const separator = getDecimalSeparator();
  const tokens = price.split(separator);
  return (
    <div css={root}>
      <Margin margin="2px 2px 0 0">
        <Text color="neutral800" type="smallPrint1Semibold">
          {currency.symbol}
        </Text>
      </Margin>

      <Text color="neutral800" type="pageHeader7">
        {tokens[0]}
      </Text>

      {tokens.length > 1 && (
        <Margin margin="4px 0 0 0">
          <Text color="neutral800" type="paragraph3Bold">
            {separator}
            {tokens[1]}
          </Text>
        </Margin>
      )}

      <Margin margin="13px 0 0 3px">
        {currency.symbol === "$" && (
          <Text color="neutral800" type="smallPrint1Bold">
            {currency.name}
          </Text>
        )}
        <Text color="neutral800" type="smallPrint1Bold">
          /mo
        </Text>
      </Margin>
    </div>
  );
};
